import {
  COMMANDS,
  ID_REGEX,
  MAX_LINE_BYTES,
  MAX_RESPONSE_LINE_BYTES,
  MAX_CAMERA_JPEG_BYTES,
  safeCopyForCode,
  type Command,
  type ResponseEnvelope,
  type WireEvent,
} from './protocol.js';
import { parseClient } from '../../protocol/messages.js';

export interface BuildRequestInput { id: string; cmd: Command; fields?: Record<string, unknown> }

export function isValidId(id: unknown): id is string {
  return typeof id === 'string' && ID_REGEX.test(id);
}

export function isCommand(value: unknown): value is Command {
  return typeof value === 'string' && (COMMANDS as readonly string[]).includes(value);
}

export class FieldError extends Error {
  readonly code = 'bad_field';
  constructor(message: string) { super(message); this.name = 'FieldError'; }
}

export function textByteLength(value: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
  let bytes = 0;
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code < 0x80) bytes++;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) { bytes += 4; index++; }
    else bytes += 3;
  }
  return bytes;
}

function validateText(value: unknown, min: number, max: number, error: string): asserts value is string {
  if (typeof value !== 'string' || /[\0\r\n]/u.test(value) || textByteLength(value) < min || textByteLength(value) > max) throw new FieldError(error);
}

export function buildRequest(input: BuildRequestInput): string {
  if (!isValidId(input.id)) throw new FieldError('Identificador no válido.');
  if (!isCommand(input.cmd)) throw new FieldError('Comando no permitido.');
  const fields = input.fields ?? {};
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) throw new FieldError('Campos no válidos.');
  const allowed: Partial<Record<Command, readonly string[]>> = {
    status: [], pair: [], 'sd.check': [], 'session.start': [], 'session.stop': [],
    'wifi.set': ['ssid', 'psk'], 'audio.enable': ['enabled'],
    'camera.capture': ['target', 'prompt'],
  };
  if (Object.keys(fields).some(key => !allowed[input.cmd]?.includes(key))) throw new FieldError('Campos no permitidos para esta operación.');
  if (input.cmd === 'wifi.set') {
    validateText(fields.ssid, 1, 32, 'Nombre de red no válido.');
    validateText(fields.psk, 8, 63, 'Contraseña de red no válida.');
  } else if (input.cmd === 'audio.enable' && typeof fields.enabled !== 'boolean') {
    throw new FieldError('Valor de audio no válido.');
  } else if (input.cmd === 'camera.capture') {
    if (fields.target !== 'preview' && fields.target !== 'live') throw new FieldError('Destino de la foto no válido.');
    if ('prompt' in fields) {
      if (fields.target !== 'live') throw new FieldError('La pregunta sólo puede acompañar una foto compartida.');
      validateText(fields.prompt, 1, 384, 'Pregunta no válida: máximo 384 bytes y una sola línea.');
      if (fields.prompt.trim() !== fields.prompt || !fields.prompt.trim()) throw new FieldError('Pregunta no válida.');
    }
  }
  const line = JSON.stringify({ id: input.id, cmd: input.cmd, ...fields });
  if (textByteLength(line) > MAX_LINE_BYTES) throw new FieldError('Petición demasiado larga.');
  return `${line}\n`;
}

export class LineFramer {
  private buffer = '';
  private bytes = 0;
  private draining = false;

  push(chunk: string): string[] {
    const lines: string[] = [];
    for (const character of chunk) {
      if (character === '\n') {
        if (!this.draining && this.buffer.length > 0) lines.push(this.buffer.endsWith('\r') ? this.buffer.slice(0, -1) : this.buffer);
        this.buffer = '';
        this.bytes = 0;
        this.draining = false;
        continue;
      }
      if (this.draining) continue;
      const size = textByteLength(character);
      this.bytes += size;
      if (this.bytes > MAX_RESPONSE_LINE_BYTES) {
        this.buffer = '';
        this.draining = true;
      } else {
        this.buffer += character;
      }
    }
    return lines;
  }

  reset(): void { this.buffer = ''; this.bytes = 0; this.draining = false; }
}

interface FrameResponse { kind: 'response'; envelope: ResponseEnvelope }
interface FrameEvent { kind: 'event'; event: WireEvent }
interface FrameUnknown { kind: 'unknown' }
export type Frame = FrameResponse | FrameEvent | FrameUnknown;

export function parseFrame(line: string): Frame | null {
  if (typeof line !== 'string' || !line.startsWith('ASIS {') || !line.endsWith('}')) return null;
  let value: unknown;
  try { value = JSON.parse(line.slice(5)); } catch { return null; }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const object = value as Record<string, unknown>;
  if ('event' in object) {
    if ('id' in object) return { kind: 'unknown' };
    if (object.event === 'state' && isText(object.phase, 32)) {
      const event: WireEvent = { event: 'state', phase: object.phase };
      if (typeof object.audio === 'boolean') event.audio = object.audio;
      if (typeof object.wifi === 'boolean') event.wifi = object.wifi;
      if (isText(object.ip, 64, true)) event.ip = object.ip;
      return { kind: 'event', event };
    }
    if (object.event === 'pairing' && validCode(object.code) && isCount(object.expiresInMs) && object.expiresInMs > 0) {
      return { kind: 'event', event: { event: 'pairing', code: object.code, expiresInMs: object.expiresInMs } };
    }
    if (object.event === 'camera_error' && (object.code === 'unsupported_image' || object.code === 'image_rate_limited')) {
      return { kind: 'event', event: { event: 'camera_error', code: object.code } };
    }
    return { kind: 'unknown' };
  }
  if (!isValidId(object.id) || typeof object.ok !== 'boolean') return { kind: 'unknown' };
  if (object.ok) {
    if (!object.result || typeof object.result !== 'object' || Array.isArray(object.result)) return { kind: 'unknown' };
    return { kind: 'response', envelope: { id: object.id, ok: true, result: object.result } };
  }
  if (!object.error || typeof object.error !== 'object' || Array.isArray(object.error)) return { kind: 'unknown' };
  const rawCode = (object.error as Record<string, unknown>).code;
  const code = typeof rawCode === 'string' ? rawCode : 'bad_request';
  return { kind: 'response', envelope: { id: object.id, ok: false, error: { code, message: safeCopyForCode(code) } } };
}

export { MAX_LINE_BYTES, MAX_RESPONSE_LINE_BYTES };

function isText(value: unknown, max: number, empty = false): value is string {
  return typeof value === 'string' && (empty || value.length > 0) && textByteLength(value) <= max && !/[\0\r\n]/u.test(value);
}
function isCount(value: unknown): value is number { return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0; }
function validCode(value: unknown): value is string { return typeof value === 'string' && /^[A-Za-z0-9]{8}$/.test(value); }

/** Select documented fields before exposing device responses to UI or callers. */
export function validateResult(command: Command, value: unknown, requestFields: Record<string, unknown> = {}): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new FieldError('Respuesta del dispositivo no válida.');
  const result = value as Record<string, unknown>;
  const output: Record<string, unknown> = {};
  const take = (key: string, valid: boolean) => { if (!valid) throw new FieldError('Respuesta del dispositivo no válida.'); output[key] = result[key]; };
  if (command === 'status') {
    take('protocol', result.protocol === 1);
    for (const key of ['deviceId', 'firmware']) take(key, isText(result[key], 128));
    take('ip', isText(result.ip, 64, true));
    take('phase', ['boot', 'idle', 'pairing', 'ready', 'error', 'blocked'].includes(String(result.phase)));
    for (const key of ['flashBytes', 'psramBytes', 'sdCapacityBytes']) take(key, isCount(result[key]));
    for (const key of ['wifiConnected', 'audioEnabled', 'sdChecked', 'sdAvailable']) take(key, typeof result[key] === 'boolean');
    for (const key of ['cameraSupported', 'cameraBusy', 'cameraLiveSupported', 'sessionActive', 'sessionReady', 'audioReady', 'capturing']) {
      if (key in result) take(key, typeof result[key] === 'boolean');
    }
    if ('cameraMaxJpegBytes' in result) take('cameraMaxJpegBytes', result.cameraMaxJpegBytes === MAX_CAMERA_JPEG_BYTES);
    if ('provider' in result) take('provider', isText(result.provider, 64, true));
    if ('heapBytes' in result) take('heapBytes', isCount(result.heapBytes));
  } else if (command === 'pair') {
    if (result.paired === true) output.paired = true;
    else { take('code', validCode(result.code)); take('expiresInMs', isCount(result.expiresInMs) && result.expiresInMs > 0); }
  } else if (command === 'wifi.set') {
    take('stored', result.stored === true); take('connected', result.connected === true);
  } else if (command === 'sd.check') {
    take('available', typeof result.available === 'boolean'); take('type', isText(result.type, 16, true));
    for (const key of ['capacityBytes', 'totalBytes', 'usedBytes']) take(key, isCount(result[key]));
  } else if (command === 'audio.enable') take('enabled', typeof result.enabled === 'boolean');
  else if (command === 'session.start') take('started', result.started === true);
  else if (command === 'session.stop') take('stopped', result.stopped === true);
  else if (command === 'camera.capture') {
    take('target', (result.target === 'preview' || result.target === 'live') && (!('target' in requestFields) || result.target === requestFields.target));
    take('mimeType', result.mimeType === 'image/jpeg');
    take('width', result.width === 320); take('height', result.height === 240);
    take('bytes', isCount(result.bytes) && result.bytes > 0 && result.bytes <= MAX_CAMERA_JPEG_BYTES);
    if (result.target === 'preview') {
      if (typeof result.data !== 'string') throw new FieldError('Respuesta del dispositivo no válida.');
      const image = decodeCameraJpeg(result.data);
      take('data', image.length === result.bytes);
    } else {
      if ('data' in result) throw new FieldError('Respuesta del dispositivo no válida.');
      take('submitted', result.submitted === true); take('promptSubmitted', typeof result.promptSubmitted === 'boolean');
    }
  }
  return output;
}

/** Bounded JPEG validation shared by response decoding and local preview. */
export function decodeCameraJpeg(data: string): Uint8Array<ArrayBuffer> {
  try {
    if (typeof data !== 'string' || !data.length || data.length > 4 * Math.ceil(MAX_CAMERA_JPEG_BYTES / 3)) throw new Error();
    // Existing browser-safe protocol parser verifies canonical base64 and JPEG structure.
    parseClient({ type: 'image', mimeType: 'image/jpeg', data });
    const bytes = Uint8Array.from(atob(data), character => character.charCodeAt(0));
    if (bytes.length > MAX_CAMERA_JPEG_BYTES) throw new Error();
    for (let offset = 2; offset + 3 < bytes.length;) {
      if (bytes[offset] !== 0xff) throw new Error();
      while (bytes[offset + 1] === 0xff) offset++;
      const marker = bytes[offset + 1]!;
      if (marker === 0xda || marker === 0xd9) break;
      if (marker === 1 || (marker >= 0xd0 && marker <= 0xd7)) { offset += 2; continue; }
      const length = (bytes[offset + 2]! << 8) | bytes[offset + 3]!;
      if (length < 2 || offset + 2 + length > bytes.length) throw new Error();
      if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
        const height = (bytes[offset + 5]! << 8) | bytes[offset + 6]!;
        const width = (bytes[offset + 7]! << 8) | bytes[offset + 8]!;
        if (width !== 320 || height !== 240) throw new Error();
        return bytes;
      }
      offset += length + 2;
    }
  } catch { /* Safe fixed error; never return image payloads in errors. */ }
  throw new FieldError('La foto recibida no es un JPEG válido de 320 × 240.');
}
